/**
 * i18n Provider — Susurro Web
 *
 * Wraps @lingui/core directly (no community fork dependency) with a
 * SolidJS-native reactive layer. Locale changes update a Signal, which
 * causes all useLingui() consumers to re-derive translations reactively
 * without any component re-render overhead.
 *
 * Lazy-loading: Only the active locale's JS catalog is fetched at startup
 * (and when the user switches language), keeping the initial bundle minimal.
 *
 * Persistence: localStorage only — the app is fully offline and accountless.
 *
 * RTL support: Sets document.documentElement.dir automatically for ar / he.
 */

import { i18n, type MessageDescriptor } from "@lingui/core";
import { generateMessageId } from "@lingui/message-utils/generateMessageId";
import {
  createContext,
  createSignal,
  onCleanup,
  onMount,
  type ParentComponent,
  useContext,
} from "solid-js";
import { RTL_LOCALES, SUPPORTED_LOCALES, type SupportedLocale } from "@susurro/config/locales";

// Initialize with a default locale so that early translation calls do not throw errors
i18n.load("en", {});
i18n.activate("en");

// ─── Types ────────────────────────────────────────────────────────────────────

interface I18nContextValue {
  /** Reactive current locale code */
  locale: () => SupportedLocale;
  /** Change locale — reloads catalog and persists to localStorage */
  setLocale: (locale: SupportedLocale) => Promise<void>;
  /** True while the catalog is loading */
  isLoading: () => boolean;
  /**
   * Reactive translation function.
   *
   * TYPE BARRIER (deliberately narrower than the implementation): it only accepts
   * `MessageDescriptor`, that is the result of a macro (`` msg`…` ``). A plain string
   * —`t("Hello")`— **does not compile**, and that is the important part: the Lingui extractor only sees
   * the macros, so a plain string never enters the catalog and stays in English in
   * the non-English locales WITHOUT any gate saying so. The implementation (`translate`)
   * still accepts a string and resolves it by hash, so that nothing breaks at runtime; the
   * type makes it impossible to write.
   */
  t: (message: MessageDescriptor, values?: Record<string, unknown>) => string;
  /**
   * Translates a DYNAMIC key: a string that is not known at the call site because it
   * comes from a data table or a function.
   *
   * It is only correct if that exact text is declared with `` msg`…` `` in a MANIFEST
   * that the extractor does see (e.g. `lib/connectivity/messages.ts`), because then the
   * id-hash matches and the catalog resolves it. Without a manifest, the string is NOT extracted and the
   * user sees it in English in the non-English locales.
   *
   * It exists so that the escape from `t`'s strict type is EXPLICIT and greppable, instead of
   * a silent `as`. Every use must point to its manifest.
   */
  tDynamic: (message: string, values?: Record<string, unknown>) => string;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const I18nContext = createContext<I18nContextValue>();

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error("useI18n must be used inside <I18nProvider>");
  }
  return ctx;
}

// ─── Catalog Loader ───────────────────────────────────────────────────────────

interface CatalogModule {
  messages: Record<string, string>;
}

function assertCatalogModule(mod: unknown): asserts mod is CatalogModule {
  if (typeof mod !== "object" || mod === null || !("messages" in mod)) {
    throw new Error("Invalid catalog module");
  }
}

/** An already imported catalog, ready to be handed to i18n. */
interface FetchedCatalog {
  /** The locale that could ACTUALLY be loaded (== the requested one, unless fallback to "en"). */
  locale: SupportedLocale;
  messages: Record<string, string>;
}

/**
 * Dynamically imports the compiled .js catalog for the requested locale.
 * Vite will code-split each locale into a separate chunk automatically.
 *
 * ⚠️ This function is PURE with respect to i18n: it imports and returns, but does NOT call
 * `i18n.load`/`i18n.activate`. It used to, and that was the bug: the
 * side effect on the i18n singleton occurred as soon as the
 * import resolved, without ANYONE being able to check whether that load was still the current one.
 * Who decides whether the result is still current is `applyLocale` (via `applyToken`),
 * and for that the side effect has to occur AFTER that check.
 */
async function fetchCatalog(locale: SupportedLocale): Promise<FetchedCatalog> {
  try {
    const rawModule: unknown = await import(`../../locales/${locale}/messages.mjs`);
    assertCatalogModule(rawModule);
    return { locale, messages: rawModule.messages };
  } catch (err) {
    console.warn(`[i18n] Failed to load catalog for "${locale}", falling back to "en"`, err);
    // Fall back to English if catalog missing
    const rawEnModule: unknown = await import("../../locales/en/messages.mjs");
    assertCatalogModule(rawEnModule);
    return { locale: "en", messages: rawEnModule.messages };
  }
}

/** Applies correct RTL/LTR direction on <html> element */
function applyDirection(locale: SupportedLocale) {
  document.documentElement.dir = RTL_LOCALES.has(locale) ? "rtl" : "ltr";
  document.documentElement.lang = locale;
}

/** Clean type-guard to verify if an unknown string represents a SupportedLocale */
function isSupportedLocale(locale: unknown): locale is SupportedLocale {
  return typeof locale === "string" && SUPPORTED_LOCALES.some((code) => code === locale);
}

/** Detect browser locale and match against supported locales */
function detectBrowserLocale(): SupportedLocale {
  const browser = navigator.language.split("-")[0]?.toLowerCase() ?? "en";
  return isSupportedLocale(browser) ? browser : "en";
}

const LOCALE_STORAGE_KEY = "@app_locale";

// ─── Provider ─────────────────────────────────────────────────────────────────

export const I18nProvider: ParentComponent = (props) => {
  const [locale, setLocaleSignal] = createSignal<SupportedLocale>("en");
  const [isLoading, setIsLoading] = createSignal(true);
  // Bumped after every REAL catalog load. Consumers (`translate`) depend on it so
  // they re-derive when a catalog finishes loading even if the `locale` signal
  // value didn't change. The module init seeds an EMPTY "en" catalog
  // (`i18n.load("en", {})`) so early calls don't throw; the first real load on
  // mount for a user whose locale IS the default "en" would set the signal to its
  // current value ("en") — a NO-OP that skips reactivity, so consumers keep the
  // empty catalog. In dev the lingui macro inlines each message's default text so
  // strings still render, but in a PRODUCTION build the macro strips those
  // defaults, so those strings render as their raw hash id. Bumping this signal
  // after each load forces the re-derive regardless of whether `locale` changed.
  const [catalogVersion, setCatalogVersion] = createSignal(0);

  // There are TWO paths that apply a language and can overlap: boot (`onMount`)
  // and the user's change (`setLocale`). Each launches an async import, and
  // the order of arrival is NOT guaranteed: if the boot import takes long (that
  // is, under load) it lands AFTER the user's change. Without a guard, that stale load
  // re-activated its language in the i18n singleton and also reverted the
  // signal and the `<html lang>` — the whole UI went back to English for no apparent reason.
  // `applyToken` makes only the MOST RECENT request reach the point of mutating anything
  // ("last write wins").
  let applyToken = 0;
  // Unmounting invalidates any in-flight `applyLocale`: its catalog import can land after the
  // provider is gone, and `i18n.activate` would then run against a torn-down tree.
  onCleanup(() => {
    applyToken++;
  });

  /**
   * Applies a locale:
   * 1. Fetches the catalog chunk (without touching i18n yet)
   * 2. Discards the result if a newer request arrived in the meantime
   * 3. Hands the catalog to i18n (load + activate)
   * 4. Updates the reactive signals + HTML dir/lang attributes
   */
  const applyLocale = async (next: SupportedLocale): Promise<void> => {
    const token = ++applyToken;
    try {
      setIsLoading(true);
      const fetched = await fetchCatalog(next); // only the import: does not touch i18n
      if (token !== applyToken) {
        // Superseded by a newer request (it owns isLoading now). The catalog is discarded
        // WITHOUT handing it to i18n: activating it here reverted the language that
        // the current request had already set (the slow boot import
        // landed after the user's change and returned the UI to English).
        return;
      }
      // The locale that ACTUALLY got in is handed over: if there was a fallback to "en", activating
      // `next` would leave i18n resolving against a catalog that does not exist.
      i18n.load(fetched.locale, fetched.messages);
      i18n.activate(fetched.locale);
      setLocaleSignal(next);
      setCatalogVersion((v) => v + 1);
      applyDirection(next);
    } finally {
      // Only the newest request owns `isLoading`: a superseded one must not clear the flag of the
      // load still in flight, and a rejected load (both catalog imports failed) must not leave it
      // stuck on.
      if (token === applyToken) {
        setIsLoading(false);
      }
    }
  };

  /**
   * Changes the active locale:
   * 1. Loads + applies the catalog (guarded against stale loads)
   * 2. Persists to localStorage
   */
  const setLocale = async (newLocale: SupportedLocale): Promise<void> => {
    if (newLocale === locale()) {
      return;
    }
    await applyLocale(newLocale);

    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, newLocale);
    } catch (err) {
      console.warn("[i18n] Could not persist locale to localStorage:", err);
    }
  };

  // On mount: load the initial locale (from localStorage or browser)
  onMount(() => {
    const init = async () => {
      try {
        const storedLocale = localStorage.getItem(LOCALE_STORAGE_KEY);
        const userLocale = isSupportedLocale(storedLocale) ? storedLocale : detectBrowserLocale();

        // `applyLocale` bumps `catalogVersion` to force the re-derive even if
        // `userLocale` is the default "en" (a set that otherwise would be a no-op and
        // would leave the whole UI showing hash ids in a production build), and
        // discards this load if the user has already chosen another language in the meantime.
        // `isLoading` is closed by it: if this load became stale, it is the
        // current request that rules, not this one.
        await applyLocale(userLocale);
      } catch (err) {
        console.warn("[i18n] Failed to initialize i18n:", err);
        setIsLoading(false);
      }
    };
    void init();
  });

  const translate = (
    message: string | MessageDescriptor,
    values?: Record<string, unknown>,
  ): string => {
    locale(); // Establish dynamic reactive dependency on SolidJS signal
    catalogVersion(); // Re-derive when a catalog finishes loading (see setLocale/onMount)
    if (typeof message === "string") {
      // The compiled catalog uses HASH ids (generateMessageId), same as the macro
      // `msg`...``. A plain string (`t("My Day")`) would look for id="My Day" and not
      // resolve (it would show English in any language). We compute the same id
      // as the macro so that `t("literal")` translates the same as `t(msg`literal`)`,
      // with the text itself as fallback. (Fix of the bug "language not reflected".)
      return i18n._({ id: String(generateMessageId(message)), message, values });
    }
    // The values also apply to descriptors (they go INSIDE the descriptor in
    // Lingui's API): without them, messages like msg`...{{date}}...`
    // rendered the empty placeholder ("{}"). The descriptor is built in
    // a separate variable: the Lingui extractor does not know how to parse an inline
    // spread inside `i18n._()` and aborts the file's extraction.
    const descriptor: MessageDescriptor = {
      ...message,
      values: { ...message.values, ...values },
    };
    return i18n._(descriptor);
  };

  return (
    <I18nContext.Provider
      value={{ locale, setLocale, isLoading, t: translate, tDynamic: translate }}
    >
      {props.children}
    </I18nContext.Provider>
  );
};
